import React, { useMemo, useRef, useState } from 'react';
import { TIMELINE_SECTIONS, VIEW_MODES, ROW_HEIGHT, SECTION_ROW_HEIGHT } from '../timeline/constants';
import {
    groupTasksByStatus,
    computeTodayOffset,
    getFullName,
    getPhotoUrl,
    getInitials,
} from '../timeline/timelineUtils';
import TimelineGanttBar from '../timeline/TimelineGanttBar';
import useTimelineState from '../timeline/useTimelineState';

// ── MiniAvatar ────────────────────────────────────────────────────────────────
function MiniAvatar({ assignee }) {
    const [imgErr, setImgErr] = useState(false);
    if (!assignee) {
        return (
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-400 flex-shrink-0 overflow-hidden">?</div>
        );
    }
    const name = getFullName(assignee);
    const photoUrl = getPhotoUrl(assignee?.photo);
    const show = photoUrl && !imgErr;
    return (
        <div
            className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden ring-2 ring-white flex items-center justify-center text-sm font-bold text-white shadow-sm"
            style={show ? {} : { background: 'linear-gradient(90deg,#03488A 0%,#00AAF1 100%)' }}
        >
            {show
                ? <img src={photoUrl} alt={name} className="w-full h-full object-cover rounded-full" onError={() => setImgErr(true)} />
                : getInitials(name)
            }
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function SprintTimeline({ tasks, onTaskClick, toggleSection, isSectionCollapsed }) {
    const {
        viewMode,
        columns,
        gridStart,
        gridEnd,
        cellWidth,
        gridWidthPx,
        goPrev,
        goNext,
        goToday,
        changeViewMode,
    } = useTimelineState();

    // ── Stage filter: clicking a pipeline section filters the Gantt ───────────
    // Clicking the same stage again clears the filter.
    const [activeFilter, setActiveFilter] = useState(null);

    const handleStageClick = (key) => {
        setActiveFilter(prev => prev === key ? null : key);
    };

    const SECTION_KEYS = TIMELINE_SECTIONS.map(s => s.key);

    // All tasks grouped for sidebar counts (always uses full task list)
    const grouped = useMemo(() => groupTasksByStatus(tasks, SECTION_KEYS), [tasks]);

    // Tasks shown in the Gantt — filtered by active stage if set
    const ganttGrouped = useMemo(() => {
        if (!activeFilter) return grouped;
        // Only show the active stage's tasks in the Gantt bars area
        const filtered = new Map(SECTION_KEYS.map(k => [k, []]));
        filtered.set(activeFilter, grouped.get(activeFilter) || []);
        return filtered;
    }, [grouped, activeFilter]);

    const todayPx = useMemo(
        () => computeTodayOffset(gridStart, gridEnd, gridWidthPx),
        [gridStart, gridEnd, gridWidthPx],
    );

    // Scroll sync refs
    const sidebarRef = useRef(null);
    const ganttRef = useRef(null);

    const handleSidebarScroll = (e) => {
        if (ganttRef.current && ganttRef.current.scrollTop !== e.target.scrollTop)
            ganttRef.current.scrollTop = e.target.scrollTop;
    };
    const handleGanttScroll = (e) => {
        if (sidebarRef.current && sidebarRef.current.scrollTop !== e.target.scrollTop)
            sidebarRef.current.scrollTop = e.target.scrollTop;
    };

    return (
        <div className="flex-1 min-w-0 bg-white dark:bg-[#1e293b] rounded-2xl border border-[#e7e8e9] dark:border-slate-700 shadow-sm flex overflow-hidden">

            {/* ── Left: Tracking Pipeline ─────────────────────────────────── */}
            <div className="w-72 border-r border-[#e7e8e9] dark:border-slate-700 bg-[#f8f9fa] dark:bg-[#0f172a] flex-shrink-0 flex flex-col">
                <div className="p-4 border-b border-[#e7e8e9] dark:border-slate-700 font-bold text-xs text-[#737782] uppercase tracking-widest flex items-center justify-between" style={{ height: SECTION_ROW_HEIGHT + 4 }}>
                    <span>Pipeline Sections</span>
                    {activeFilter && (
                        <button
                            onClick={() => setActiveFilter(null)}
                            className="text-sm font-bold text-blue-600 hover:underline"
                        >
                            Clear filter
                        </button>
                    )}
                </div>

                <div
                    className="divide-y divide-[#e7e8e9] dark:divide-slate-700 flex-1 overflow-y-auto custom-scrollbar"
                    ref={sidebarRef}
                    onScroll={handleSidebarScroll}
                >
                    {TIMELINE_SECTIONS.map((section) => {
                        const sectionTasks = grouped.get(section.key) || [];
                        const collapsed = isSectionCollapsed(section.key);
                        const isFiltered = activeFilter === section.key;

                        return (
                            <div key={section.key}>
                                {/* Section header */}
                                <div
                                    className={`px-3 flex items-center justify-between cursor-pointer transition-colors ${isFiltered
                                            ? 'bg-blue-50 dark:bg-slate-700'
                                            : 'hover:bg-[#e7e8e9] dark:hover:bg-slate-700'
                                        }`}
                                    style={{
                                        height: SECTION_ROW_HEIGHT,
                                        borderLeft: `3px solid ${isFiltered ? section.accentColor : (!collapsed ? section.accentColor : 'transparent')}`,
                                    }}
                                    onClick={() => {
                                        toggleSection(section.key);
                                        handleStageClick(section.key);
                                    }}
                                >
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="material-symbols-outlined text-sm transition-transform duration-200"
                                            style={{
                                                transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                                                fontFamily: 'Material Symbols Outlined',
                                            }}
                                        >chevron_right</span>
                                        <span className={`text-xs font-bold ${isFiltered ? 'text-blue-700 dark:text-blue-300' : 'text-[#191c1d] dark:text-white'}`}>
                                            {section.label}
                                        </span>
                                    </div>
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded min-w-[22px] text-center ${section.badgeBg} ${section.badgeText}`}>
                                        {String(sectionTasks.length).padStart(2, '0')}
                                    </span>
                                </div>

                                {/* Task rows */}
                                {!collapsed && sectionTasks.map(task => (
                                    <div
                                        key={task._id}
                                        className="px-4 flex items-center gap-3 border-b border-gray-100 dark:border-slate-800 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer transition-colors group"
                                        style={{ height: ROW_HEIGHT }}
                                        onClick={() => onTaskClick?.(task)}
                                    >
                                        <MiniAvatar assignee={task.assignee} />
                                        <div className="flex-1 min-w-0">
                                            <p className="truncate text-xs font-semibold text-[#191c1d] dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300">
                                                {task.title}
                                            </p>
                                            {task.project?.name && (
                                                <p className="truncate text-sm text-[#737782] dark:text-slate-400">
                                                    {task.project.name}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ── Center: Sprint Timeline ──────────────────────────────────── */}
            <div className="flex-1 flex flex-col overflow-hidden relative">

                {/* Date header placeholder */}
                <div
                    className="flex-shrink-0 border-b border-[#e7e8e9] dark:border-slate-700 bg-[#f8f9fa] dark:bg-[#0f172a]"
                    style={{ height: SECTION_ROW_HEIGHT + 4 }}
                />

                {/* Scrollable area */}
                <div
                    className="flex-1 overflow-auto relative custom-scrollbar"
                    ref={ganttRef}
                    onScroll={handleGanttScroll}
                >
                    <div style={{ minWidth: gridWidthPx }}>

                        {/* Sticky date column header */}
                        <div
                            className="sticky top-0 z-10 flex border-b border-[#e7e8e9] dark:border-slate-700 bg-[#f8f9fa] dark:bg-[#0f172a]"
                            style={{ height: SECTION_ROW_HEIGHT + 4, marginTop: -(SECTION_ROW_HEIGHT + 4) }}
                        >
                            {columns.map(col => (
                                <div
                                    key={col.key}
                                    className={`flex-1 flex flex-col items-center justify-center ${col.isToday ? 'bg-blue-50/50' : ''}`}
                                    style={{ minWidth: cellWidth }}
                                >
                                    <span className={`text-xs font-bold ${col.isToday ? 'text-blue-600' : 'text-[#737782]'}`}>
                                        {col.label}
                                    </span>
                                    {col.isToday && (
                                        <span className="text-[8px] font-black text-blue-600 uppercase tracking-widest">Today</span>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Gantt bars */}
                        <div style={{ minHeight: '100%', paddingBottom: 64 }} className="relative">

                            {/* Today line */}
                            {todayPx !== null && (
                                <div
                                    className="absolute top-0 bottom-0 z-20 flex flex-col items-center"
                                    style={{ left: todayPx, width: 1, backgroundColor: 'rgba(3,72,138,0.3)' }}
                                >
                                    <div className="bg-[#03488A] text-white text-[8px] font-bold px-1.5 py-0.5 rounded-b mt-1">TODAY</div>
                                </div>
                            )}

                            {/* Section rows — uses ganttGrouped so filter applies */}
                            {TIMELINE_SECTIONS.map((section) => {
                                const sectionTasks = ganttGrouped.get(section.key) || [];
                                const collapsed = isSectionCollapsed(section.key);
                                return (
                                    <div
                                        key={section.key}
                                        style={{ opacity: activeFilter && activeFilter !== section.key ? 0.15 : 1, transition: 'opacity 150ms ease' }}
                                    >
                                        <div className="border-b border-[#e7e8e9]" style={{ height: SECTION_ROW_HEIGHT }} />
                                        {!collapsed && sectionTasks.map(task => (
                                            <div
                                                key={task._id}
                                                className="relative hover:bg-blue-50/20"
                                                style={{ height: ROW_HEIGHT }}
                                            >
                                                <TimelineGanttBar
                                                    task={task}
                                                    section={section}
                                                    gridStart={gridStart}
                                                    gridEnd={gridEnd}
                                                    gridWidthPx={gridWidthPx}
                                                    onTaskClick={onTaskClick}
                                                />
                                            </div>
                                        ))}
                                        {!collapsed && sectionTasks.length === 0 && (
                                            <div style={{ height: ROW_HEIGHT }} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Floating controls */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-white/90 dark:bg-[#1e293b]/90 backdrop-blur px-3 py-2 rounded-xl border border-[#e7e8e9] dark:border-slate-700 shadow-lg">
                    <div className="flex bg-gray-100 dark:bg-slate-700 p-1 rounded-lg">
                        {VIEW_MODES.map(mode => (
                            <button
                                key={mode.key}
                                onClick={() => changeViewMode(mode.key)}
                                className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${viewMode === mode.key
                                        ? 'bg-white dark:bg-[#334155] shadow text-[#003263] dark:text-[#a7c8ff]'
                                        : 'text-gray-500 dark:text-slate-400 hover:text-[#003263] dark:hover:text-white'
                                    }`}
                            >
                                {mode.label}
                            </button>
                        ))}
                    </div>
                    <div className="w-px h-4 bg-gray-300 dark:bg-slate-600 mx-1" />
                    <div className="flex items-center gap-1">
                        <button onClick={goPrev} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700">
                            <span className="material-symbols-outlined text-sm" style={{ fontFamily: 'Material Symbols Outlined' }}>chevron_left</span>
                        </button>
                        <button onClick={goToday} className="px-3 py-1 text-xs font-bold border border-[#e7e8e9] dark:border-slate-600 rounded bg-white dark:bg-[#334155] hover:bg-gray-50 dark:hover:bg-slate-700 text-[#003263] dark:text-[#a7c8ff]">
                            Today
                        </button>
                        <button onClick={goNext} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700">
                            <span className="material-symbols-outlined text-sm" style={{ fontFamily: 'Material Symbols Outlined' }}>chevron_right</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
